/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { useEffect, useRef, useState } from 'react';
import propTypes from 'prop-types';
import * as echarts from 'echarts/lib/echarts';

// Misc imports
import walden from '../../assets/chartTheme/walden';

const EchartsWrapper = ({
  chartOptions = {},
  style = {},
  externalClassname = '',
  optsSettings = {
    renderer: 'svg',
    devicePixelRatio: 2,
    useDirtyRect: true,
    useCoarsePointer: true
  }
}) => {
  const chartRef = useRef(null);
  const [chart, setChart] = useState(null);

  const resizeObserver = new ResizeObserver(entries => {
    const [entry] = entries;
    if (entry && entry.target) {
      chart.resize(
        {
          width: entry.target.clientWidth,
          height: entry.target.clientHeight
        }
      );
    }
  });

  // Initialize the chart
  useEffect(() => {
    if (chartRef.current && !chart) {
      // Delay initialization slightly to ensure container has stable dimensions and avoid resizing
      const timer = setTimeout(() => {
        const chartInstance = echarts.init(chartRef.current, walden, optsSettings);
        setChart(chartInstance);
      }, 150);

      return () => clearTimeout(timer);
    }

    return () => {
      if (chart && !chartRef.current) {
        chart.dispose(() => {
          setChart(null);
        });
      }
    };
  }, [chart, chartRef.current]);

  // Update the chart options
  useEffect(() => {
    if (chart) {
      chart.setOption(chartOptions);

      chart.on('finished', () => {
        if(chart && chartRef.current) {
          resizeObserver?.observe?.(chartRef.current);
        }
      });
    }

    return () => {
      if (chart) {
        chart.off('finished');
      }

      resizeObserver?.disconnect?.();
    };
  }, [chartOptions, chart, chartRef.current]);

  return (
    <div
      ref={chartRef}
      className={`w-100 h-100 ${externalClassname}`}
      style={style}
    />
  );
};

EchartsWrapper.propTypes = {
  chartOptions: propTypes.object.isRequired,
  optsSettings: propTypes.object,
  loading: propTypes.bool,
  theme: propTypes.string,
  style: propTypes.object,
  externalClassname: propTypes.string
};

export default EchartsWrapper;
